export function hasSavedClubView(mapInfo) {
  const state = mapInfo?.map_state;
  return Array.isArray(state?.center)
    && state.center.length === 2
    && typeof state.zoom === 'number';
}

/** First land, or land save with no canonical camera yet. */
export function shouldEnterConfirmClubView({
  isPresident,
  tractCount,
  hasSavedView,
  confirming,
}) {
  return Boolean(isPresident)
    && Number(tractCount || 0) > 0
    && !hasSavedView
    && !confirming;
}

/** Saved view is the widest extent; gestures stay on. */
export function clubBrowseExtentActive({ confirming, hasSavedView }) {
  return Boolean(hasSavedView) && !confirming;
}

export function normalizeClubViewBounds(raw) {
  if (!Array.isArray(raw) || raw.length !== 4) return null;
  const [west, south, east, north] = raw;
  if (![west, south, east, north].every((n) => typeof n === 'number' && !Number.isNaN(n))) {
    return null;
  }
  if (east <= west || north <= south) return null;
  return [west, south, east, north];
}

/** Slack so pitch/rotate at the home view does not fight exact pixel bounds. */
export function padClubViewBounds(bbox, factor = 0.08) {
  const bounds = normalizeClubViewBounds(bbox);
  if (!bounds) return null;
  const [west, south, east, north] = bounds;
  const lngPad = Math.max(east - west, 1e-6) * factor;
  const latPad = Math.max(north - south, 1e-6) * factor;
  return [west - lngPad, south - latPad, east + lngPad, north + latPad];
}

export function clubViewMaxBounds(bbox) {
  const padded = padClubViewBounds(bbox);
  if (!padded) return null;
  return [[padded[0], padded[1]], [padded[2], padded[3]]];
}

export function clubViewBoundsFromMap(map) {
  if (!map || typeof map.getBounds !== 'function') return null;
  try {
    const bounds = map.getBounds();
    if (!bounds) return null;
    const west = typeof bounds.getWest === 'function' ? bounds.getWest() : bounds._sw?.lng;
    const south = typeof bounds.getSouth === 'function' ? bounds.getSouth() : bounds._sw?.lat;
    const east = typeof bounds.getEast === 'function' ? bounds.getEast() : bounds._ne?.lng;
    const north = typeof bounds.getNorth === 'function' ? bounds.getNorth() : bounds._ne?.lat;
    return normalizeClubViewBounds([west, south, east, north]);
  } catch (_) {
    return null;
  }
}

export function clubCameraStateFromMap(map) {
  if (!map || typeof map.getCenter !== 'function' || typeof map.getZoom !== 'function') {
    return null;
  }
  const center = map.getCenter();
  const lng = typeof center?.lng === 'number' ? center.lng : null;
  const lat = typeof center?.lat === 'number' ? center.lat : null;
  const zoom = map.getZoom();
  if (lng == null || lat == null || typeof zoom !== 'number') return null;
  const bounds = clubViewBoundsFromMap(map);
  return {
    center: [lng, lat],
    zoom,
    bearing: typeof map.getBearing === 'function' ? map.getBearing() : 0,
    pitch: typeof map.getPitch === 'function' ? map.getPitch() : 0,
    ...(bounds ? { bounds } : {}),
  };
}

export function applyClubViewExtent(map, state, { captureLiveBounds = false } = {}) {
  if (!map || !state) return false;
  const minZoom = typeof state.zoom === 'number' ? state.zoom : null;
  let bounds = normalizeClubViewBounds(state.bounds)
    || normalizeClubViewBounds(map.__yona_clubViewBounds);
  if (!bounds && captureLiveBounds) {
    bounds = clubViewBoundsFromMap(map);
  }
  if (bounds) {
    try { map.__yona_clubViewBounds = bounds; } catch (_) {}
  }
  const maxBounds = clubViewMaxBounds(bounds);
  if (minZoom != null && typeof map.setMinZoom === 'function') {
    try { map.setMinZoom(minZoom); } catch (_) {}
  }
  if (maxBounds && typeof map.setMaxBounds === 'function') {
    try { map.setMaxBounds(maxBounds); } catch (_) {}
  }
  return Boolean(minZoom != null || maxBounds);
}

export function clearClubViewExtent(map) {
  if (!map) return;
  try {
    if (typeof map.setMaxBounds === 'function') map.setMaxBounds(null);
  } catch (_) {}
  try {
    if (typeof map.setMinZoom === 'function') map.setMinZoom(0);
  } catch (_) {}
}
